import { useEffect, useRef } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AuthLayout } from '../components/AuthLayout'
import { ButtonLink, Spinner } from '../components/ui'
import { api, call } from '../lib/api'
import { meQuery } from '../lib/queries'

// The "Stop these emails" link of a notification email
// (designs/20261019-notification-settings.md §4): no sign-in, the signed
// token turns one category's email off, and the page says which.
export const Route = createFileRoute('/unsubscribe')({
  validateSearch: (s: Record<string, unknown>): { t?: string } => ({ t: typeof s.t === 'string' ? s.t : undefined }),
  component: Unsubscribe,
})

function Unsubscribe() {
  const { t } = Route.useSearch()
  const qc = useQueryClient()
  const stop = useMutation({
    mutationFn: () => call(api.POST('/notifications/unsubscribe', { body: { token: t ?? '' } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: meQuery.queryKey }),
  })
  const fired = useRef(false)
  useEffect(() => {
    if (t && !fired.current) {
      fired.current = true
      stop.mutate()
    }
  }, [t, stop])

  if (stop.isSuccess) {
    return (
      <AuthLayout title="Emails off" lede={`You won't get ${stop.data.label.toLowerCase()} emails any more. Everything else is where it was.`}>
        <ButtonLink to="/account" hash="notifications" variant="primary" block>All notification settings</ButtonLink>
      </AuthLayout>
    )
  }
  if (!t || stop.isError) {
    return (
      <AuthLayout title="That link didn't work" lede="It may have been cut short by your mail app. You can turn any email off on your account page.">
        <ButtonLink to="/account" hash="notifications" variant="primary" block>Notification settings</ButtonLink>
      </AuthLayout>
    )
  }
  return (
    <AuthLayout title="Turning that off…"><span className="text-muted"><Spinner /></span></AuthLayout>
  )
}
